import * as React from "react";
import { ChevronLeft, ChevronRight, Play, Pause, Film } from "lucide-react";

const useIsoLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

// 3D coverflow-style carousel. Straight port of the styles.refero.design pattern.
// Paints transforms directly to the DOM (no React re-render per frame).
export function CoverflowCarousel({
  slides,
  rotate = 44,
  depth = 0.6,
  perspective = 3,
  falloff = 0.56,
  fade = 0.1,
  cardWidth = "clamp(180px, 26vw, 320px)",
  gap = 0.05,
  loop = true,
  showCaption = false,
  showPagination = false,
  showNavigation = false,
  onIndexChange,
  cinemaEnabled = false,
  cinemaDefaultOn = false,
  cinemaMs = 3200,
  label = "Cover carousel",
  className = "",
  cardClassName = "",
}) {
  const count = slides.length;
  const frameRef = React.useRef(null);
  const cardRefs = React.useRef([]);
  const posRef = React.useRef(0);
  const targetRef = React.useRef(0);
  const widthRef = React.useRef(0);
  const rafRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const [selected, setSelected] = React.useState(0);
  const [cinema, setCinema] = React.useState(!!(cinemaEnabled && cinemaDefaultOn));
  const cinemaTimer = React.useRef(null);

  // Cinema Mode: auto-advance + Ken-Burns overlay animates on each slide change
  React.useEffect(() => {
    if (!cinema) {
      if (cinemaTimer.current) { clearInterval(cinemaTimer.current); cinemaTimer.current = null; }
      return;
    }
    cinemaTimer.current = setInterval(() => {
      // nudge forward
      const step = 1;
      const nextTarget = (loop ? posRef.current : Math.max(0, Math.min(count - 1, posRef.current))) + step;
      const target = loop ? nextTarget : Math.max(0, Math.min(count - 1, nextTarget));
      // Use the settle path
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      targetRef.current = target;
      const nextIdx = ((Math.round(target) % count) + count) % count;
      setSelected(nextIdx);
      if (onIndexChange) onIndexChange(nextIdx);
      const stepFn = () => {
        const remaining = target - posRef.current;
        if (Math.abs(remaining) < 0.0004) { posRef.current = target; paint(); rafRef.current = null; return; }
        posRef.current += remaining * 0.16;
        paint();
        rafRef.current = requestAnimationFrame(stepFn);
      };
      rafRef.current = requestAnimationFrame(stepFn);
    }, cinemaMs);
    return () => { if (cinemaTimer.current) { clearInterval(cinemaTimer.current); cinemaTimer.current = null; } };
  }, [cinema, cinemaMs, count, loop]); // eslint-disable-line

  React.useEffect(() => {
    if (!cinemaEnabled) setCinema(false);
  }, [cinemaEnabled]);

  const indexAt = React.useCallback(
    (pos) => ((Math.round(pos) % count) + count) % count,
    [count]
  );

  const paint = React.useCallback(() => {
    const width = widthRef.current;
    if (!width) return;
    const pitch = width * (1 + gap);
    const pos = posRef.current;
    cardRefs.current.forEach((card, index) => {
      if (!card) return;
      let offset = index - pos;
      if (loop) {
        offset = ((offset % count) + count) % count;
        if (offset > count / 2) offset -= count;
      }
      const distance = Math.abs(offset);
      const ramp = Math.pow(distance, falloff);
      const tilt = Math.min(rotate * ramp, 82) * Math.sign(offset);
      card.style.transform =
        `translateX(calc(-50% + ${offset * pitch}px)) ` +
        `translateZ(${-depth * width * ramp}px) rotateY(${-tilt}deg)`;
      const edge = loop ? Math.min(1, Math.max(0, count / 2 - distance)) : 1;
      card.style.opacity = String(Math.max(0, 1 - fade * distance) * edge);
      card.style.zIndex = String(100 - Math.round(distance));
    });
  }, [count, depth, fade, falloff, gap, loop, rotate]);

  const settle = React.useCallback(
    (target) => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      targetRef.current = target;
      const nextIdx = indexAt(target);
      setSelected(nextIdx);
      if (onIndexChange) onIndexChange(nextIdx);
      const step = () => {
        const remaining = target - posRef.current;
        if (Math.abs(remaining) < 0.0004) {
          posRef.current = target;
          paint();
          rafRef.current = null;
          return;
        }
        posRef.current += remaining * 0.16;
        paint();
        rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    },
    [indexAt, paint]
  );

  const clamp = React.useCallback(
    (pos) => (loop ? pos : Math.max(0, Math.min(count - 1, pos))),
    [count, loop]
  );

  const goTo = React.useCallback(
    (index) => {
      const target = loop
        ? index + Math.round((targetRef.current - index) / count) * count
        : index;
      settle(clamp(target));
    },
    [clamp, count, loop, settle]
  );

  const nudge = React.useCallback(
    (by) => settle(clamp(Math.round(targetRef.current) + by)),
    [clamp, settle]
  );

  const onPointerDown = (event) => {
    // Manual interaction — pause cinema mode
    if (cinema) setCinema(false);
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    targetRef.current = posRef.current;
    dragRef.current = {
      id: event.pointerId,
      x: event.clientX,
      pos: posRef.current,
      v: 0,
      t: performance.now(),
    };
  };
  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    const pitch = widthRef.current * (1 + gap);
    if (!pitch) return;
    const now = performance.now();
    const previous = posRef.current;
    posRef.current = clamp(drag.pos - (event.clientX - drag.x) / pitch);
    drag.v = ((posRef.current - previous) / Math.max(now - drag.t, 1)) * 1000;
    drag.t = now;
    const index = indexAt(posRef.current);
    if (index !== selected) { setSelected(index); if (onIndexChange) onIndexChange(index); }
    paint();
  };
  const endDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    dragRef.current = null;
    const carried = Math.max(-2, Math.min(2, drag.v * 0.18));
    settle(clamp(Math.round(posRef.current + carried)));
  };

  useIsoLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const measure = () => {
      const card = cardRefs.current[0];
      if (!card) return;
      widthRef.current = card.offsetWidth;
      paint();
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [paint]);

  React.useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  const active = slides[selected];

  return (
    <div
      className={`w-full ${className}`}
      style={{ "--cf-card": cardWidth }}
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      data-testid="coverflow-carousel"
    >
      <div className="relative">
        <div
          ref={frameRef}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") { event.preventDefault(); if (cinema) setCinema(false); nudge(-1); }
            else if (event.key === "ArrowRight") { event.preventDefault(); if (cinema) setCinema(false); nudge(1); }
          }}
          className="cursor-grab overflow-hidden py-10 outline-none focus-visible:ring-2 focus-visible:ring-[#A7C7E7] active:cursor-grabbing"
          style={{
            perspective: `calc(var(--cf-card) * ${perspective})`,
            touchAction: "pan-y",
          }}
        >
          <div
            className="relative select-none"
            style={{ height: "var(--cf-card)", transformStyle: "preserve-3d" }}
          >
            {slides.map((slide, index) => {
              const isVideo = slide.type === "video";
              const posterSrc = slide.poster || slide.src;
              return (
              <div
                key={index}
                ref={(node) => { cardRefs.current[index] = node; }}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${count}`}
                className={`absolute left-1/2 top-0 aspect-square overflow-hidden rounded-2xl bg-[#EAF0F6] shadow-[0_30px_60px_-15px_rgba(11,25,44,0.55)] will-change-transform ${cardClassName}`}
                style={{ width: "var(--cf-card)" }}
                data-testid={`coverflow-slide-${index}`}
                data-cf-active={index === selected ? "true" : "false"}
              >
                {isVideo ? (
                  <video
                    src={slide.src}
                    poster={slide.poster || undefined}
                    muted
                    playsInline
                    preload="metadata"
                    draggable={false}
                    className="h-full w-full select-none object-cover pointer-events-none"
                  />
                ) : (
                  <img
                    src={posterSrc}
                    alt={slide.alt}
                    draggable={false}
                    className="h-full w-full select-none object-cover"
                    loading={index < 3 ? "eager" : "lazy"}
                  />
                )}
                {/* Video badge */}
                {isVideo && (
                  <>
                    <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                        <Play className="w-5 h-5 text-[#0B192C]" fill="#0B192C" />
                      </span>
                    </div>
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-black/55 backdrop-blur px-2 py-1 text-[10px] font-body uppercase tracking-[0.2em] text-white">
                      <Film className="w-3 h-3" /> Reel
                    </span>
                  </>
                )}
                {/* Ken-Burns overlay — only rendered on the active card while Cinema is on */}
                {cinema && index === selected && !isVideo && (
                  <img
                    key={`kb-${selected}`}
                    src={posterSrc}
                    alt=""
                    aria-hidden
                    draggable={false}
                    className="absolute inset-0 h-full w-full object-cover pointer-events-none coverflow-kenburns"
                    style={{ animationDuration: `${cinemaMs}ms` }}
                  />
                )}
              </div>
              );
            })}
          </div>
        </div>

        {showNavigation && (
          <>
            <button
              type="button"
              aria-label="Previous slide"
              onClick={() => { if (cinema) setCinema(false); nudge(-1); }}
              className="absolute left-3 top-1/2 z-[200] -translate-y-1/2 rounded-full bg-white/85 hover:bg-white p-2 text-[#0B192C] backdrop-blur transition shadow-md"
              data-testid="coverflow-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              aria-label="Next slide"
              onClick={() => { if (cinema) setCinema(false); nudge(1); }}
              className="absolute right-3 top-1/2 z-[200] -translate-y-1/2 rounded-full bg-white/85 hover:bg-white p-2 text-[#0B192C] backdrop-blur transition shadow-md"
              data-testid="coverflow-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {cinemaEnabled && (
          <button
            type="button"
            onClick={() => setCinema((v) => !v)}
            data-testid="coverflow-cinema-toggle"
            aria-pressed={cinema}
            className={`absolute right-3 bottom-3 z-[200] inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-body backdrop-blur transition shadow-md ${
              cinema
                ? "bg-[#0B192C] text-white"
                : "bg-white/85 text-[#0B192C] hover:bg-white"
            }`}
            title={cinema ? "Pause cinema mode" : "Play as slideshow"}
          >
            {cinema ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {cinema ? "Cinema · playing" : "Cinema mode"}
          </button>
        )}
      </div>

      {showCaption && active?.title && (
        <div key={selected} className="mt-2 flex flex-col items-center px-6 duration-300 animate-in fade-in">
          <p className="font-display italic text-2xl sm:text-3xl text-[#0B192C]">{active.title}</p>
          {active.subtitle && (
            <p className="mt-1 font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">{active.subtitle}</p>
          )}
        </div>
      )}

      {showPagination && (
        <div className="mt-4 flex items-center justify-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === selected}
              onClick={() => goTo(index)}
              className={`h-2 rounded-full bg-[#0B192C] transition-all ${
                index === selected ? "opacity-100 w-6" : "opacity-30 w-2"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
